import { usePersistedState } from "../lib/usePersistedState";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, GripVertical, Clock, AlertTriangle, CalendarDays, Trash2, User, Phone, Mail as MailIcon, Search, LayoutGrid, Table as TableIcon } from "lucide-react";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { useEntity, createItem, updateItem, deleteItem } from "../hooks/useEntity";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const RESOURCE = "acquisition_pipeline";
export const ACQ_RESOURCE = RESOURCE;

// Match the Property Analysis (leads) list columns: Ranked · Status · Project Name · City · State · Owner · Verdict.
const VERDICT_PILL = {
  "GO": "bg-emerald-500/15 text-emerald-500",
  "CAUTION": "bg-amber-500/15 text-amber-600",
  "NO-GO": "bg-red-500/15 text-red-500",
};
const VERDICT_COLUMN = {
  key: "angela_verdict", label: "Verdict",
  render: (r) => r.angela_verdict
    ? <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${VERDICT_PILL[r.angela_verdict] || "bg-secondary text-muted-foreground"}`} data-testid={`verdict-badge-${r.id}`}>{r.angela_verdict}</span>
    : <span className="text-muted-foreground">—</span>,
};
const BASE_COLUMNS = [
  { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status || r.lead_status || "New"} /> },
  { key: "project_name", label: "Project Name", render: (r) => r.property_label || r.property_name || r.property_analysis_name || r.project_name || `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.street_address || r.address || "—" },
  { key: "city", label: "City", render: (r) => r.city || "—" },
  { key: "state", label: "State", render: (r) => r.state || "—" },
  { key: "owner_name", label: "Owner", render: (r) => r.owner_name || r.assigned_to_name || "Unassigned" },
];

export const PIPELINE_STAGES = [
  { key: "Under Contract", accent: "border-emerald-500/50" },
  { key: "Due Diligence", accent: "border-sky-500/50" },
  { key: "Feasibility", accent: "border-cyan-500/50" },
  { key: "Financial Review", accent: "border-indigo-500/50" },
  { key: "Approved", accent: "border-teal-500/50" },
  { key: "Not Approved", accent: "border-rose-500/50" },
  { key: "Acquired", accent: "border-green-500/50" },
  { key: "Converted", accent: "border-primary/50" },
];
export const STAGE_KEYS = PIPELINE_STAGES.map((s) => s.key);
export const DEAD_STAGES = ["Not Approved", "Acquired", "Converted"];
export const OUTREACH_METHODS = ["Direct Mail", "Cold Call", "Text", "Email", "Door Knock", "Agent / MLS", "Skip Trace", "Other"];
export const NEXT_ACTIONS = ["Send Mailer", "Call Owner", "Send Follow-Up Letter", "Research Owner", "Verify Phone Number", "Prepare Offer", "Follow Up on Offer", "Schedule Site Visit", "Other"];
export const SELLER_MOTIVATION = ["Unknown", "Low", "Medium", "High"];
export const SELLER_RESPONSE = ["No Response", "Not Interested", "Interested", "Callback Requested", "Wants Offer", "Countered"];

export const todayKey = () => new Date().toISOString().slice(0, 10);
export const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));
export const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d.length <= 10 ? d + "T00:00:00" : d);
  if (isNaN(dt)) return "—";
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
export const money = (v) => {
  const n = Number(v);
  if (!n) return "—";
  return "$" + Math.round(n).toLocaleString();
};
const logEntry = (text, type, actor) => ({ id: uid(), date: new Date().toISOString(), text, type: type || "note", actor: actor || "" });
export { logEntry };

const Field = ({ label, children }) => (
  <div><div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{label}</div>{children}</div>
);

// -----------------------------------------------------------------------------
function DealDialog({ open, onOpenChange, deal, users, currentUser, onSaved, onDelete }) {
  const editing = !!deal;
  const blank = {
    property_label: "", owner: "", address: "", status: "Under Contract",
    outreach_method: "Direct Mail", last_contact: "", next_action: "Send Mailer",
    next_action_date: todayKey(), assigned_to: currentUser?.id || "",
    assigned_to_name: currentUser?.name || currentUser?.email || "", attempts: 0,
    seller_response: "No Response", seller_motivation: "Unknown",
    target_offer: "", max_offer: "", notes: "",
  };
  const [form, setForm] = useState(blank);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setForm(editing ? { ...blank, ...deal } : blank); setNote(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deal]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAssigned = (id) => {
    const u = users.find((x) => x.id === id);
    setForm((f) => ({ ...f, assigned_to: id, assigned_to_name: u ? (u.name || u.email) : "" }));
  };

  const save = async () => {
    if (!form.property_label.trim()) return toast.error("Property / APN is required");
    if (!form.next_action) return toast.error("Next Action is required");
    if (!form.next_action_date) return toast.error("Next Action Date is required");
    setBusy(true);
    try {
      const payload = { ...form, attempts: Number(form.attempts) || 0 };
      if (!editing) {
        payload.activity = [logEntry("Deal added to pipeline", "system", currentUser?.name)];
        const created = await createItem(RESOURCE, payload);
        toast.success("Deal added");
        onSaved(created);
      } else {
        const activity = Array.isArray(deal.activity) ? [...deal.activity] : [];
        if (deal.status !== form.status) activity.push(logEntry(`Status: ${deal.status} → ${form.status}`, "status", currentUser?.name));
        if (Number(deal.target_offer) !== Number(form.target_offer) && form.target_offer) activity.push(logEntry(`Target offer set to ${money(form.target_offer)}`, "offer", currentUser?.name));
        if (Number(deal.max_offer) !== Number(form.max_offer) && form.max_offer) activity.push(logEntry(`Max offer set to ${money(form.max_offer)}`, "offer", currentUser?.name));
        if (note.trim()) activity.push(logEntry(note.trim(), "note", currentUser?.name));
        payload.activity = activity;
        const updated = await updateItem(RESOURCE, deal.id, payload);
        toast.success("Deal updated");
        onSaved(updated);
      }
      onOpenChange(false);
    } catch (e) { toast.error("Could not save deal"); }
    finally { setBusy(false); }
  };

  const timeline = [...(deal?.activity || [])].sort((a, b) => new Date(b.date) - new Date(a.date));

  const sel = (val, onCh, opts, tid) => (
    <Select value={val} onValueChange={onCh}>
      <SelectTrigger data-testid={tid}><SelectValue /></SelectTrigger>
      <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="deal-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Deal" : "Add Deal"}</DialogTitle>
          <DialogDescription>{editing ? "Update outreach details and log activity." : "Just add the property to get it on the board — open the card anytime to fill in the rest."}</DialogDescription>
        </DialogHeader>

        {!editing ? (
          <div className="space-y-3">
            <Field label="Property / APN *"><Input value={form.property_label} onChange={(e) => set("property_label", e.target.value)} placeholder="341-115-006 or 0 Tepee Ln" autoFocus data-testid="deal-property" /></Field>
            <Field label="Owner"><Input value={form.owner} onChange={(e) => set("owner", e.target.value)} placeholder="John Smith (optional)" data-testid="deal-owner" /></Field>
            <p className="text-xs text-muted-foreground">Starts in <span className="text-foreground/80">Not Contacted</span>, assigned to you, with a follow-up today. Open the card to add outreach details later.</p>
          </div>
        ) : (
        <>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Property / APN *"><Input value={form.property_label} onChange={(e) => set("property_label", e.target.value)} placeholder="341-115-006 or 0 Tepee Ln" data-testid="deal-property" /></Field>
          <Field label="Owner"><Input value={form.owner} onChange={(e) => set("owner", e.target.value)} placeholder="John Smith" data-testid="deal-owner" /></Field>
          <Field label="Address"><Input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Street / City" /></Field>
          <Field label="Outreach Status">{sel(form.status, (v) => set("status", v), STAGE_KEYS, "deal-status")}</Field>
          <Field label="Outreach Method">{sel(form.outreach_method, (v) => set("outreach_method", v), OUTREACH_METHODS)}</Field>
          <Field label="Assigned To">
            <Select value={form.assigned_to} onValueChange={setAssigned}>
              <SelectTrigger data-testid="deal-assigned"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Next Action *">{sel(form.next_action, (v) => set("next_action", v), NEXT_ACTIONS, "deal-next-action")}</Field>
          <Field label="Next Action Date *"><Input type="date" value={form.next_action_date || ""} onChange={(e) => set("next_action_date", e.target.value)} data-testid="deal-next-date" /></Field>
          <Field label="Last Contact"><Input type="date" value={form.last_contact || ""} onChange={(e) => set("last_contact", e.target.value)} /></Field>
          <Field label="Attempts"><Input type="number" min="0" value={form.attempts} onChange={(e) => set("attempts", e.target.value)} /></Field>
          <Field label="Seller Response">{sel(form.seller_response, (v) => set("seller_response", v), SELLER_RESPONSE)}</Field>
          <Field label="Seller Motivation">{sel(form.seller_motivation, (v) => set("seller_motivation", v), SELLER_MOTIVATION)}</Field>
          <Field label="Target Offer"><Input type="number" value={form.target_offer} onChange={(e) => set("target_offer", e.target.value)} placeholder="125000" /></Field>
          <Field label="Maximum Offer"><Input type="number" value={form.max_offer} onChange={(e) => set("max_offer", e.target.value)} placeholder="145000" /></Field>
        </div>
        <Field label="Notes"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} placeholder="Long-term absentee owner…" /></Field>

          <div className="border-t border-border pt-3">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Log Activity</div>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="e.g. Called owner — left voicemail; Seller countered $155K" data-testid="deal-log-note" />
            <div className="mt-3 text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Timeline</div>
            <div className="space-y-2 max-h-52 overflow-y-auto pr-1" data-testid="deal-timeline">
              {timeline.length === 0 && <div className="text-sm text-muted-foreground">No activity yet.</div>}
              {timeline.map((a) => (
                <div key={a.id} className="flex gap-3 text-sm">
                  <div className="w-24 shrink-0 text-muted-foreground">{fmtDate(a.date)}</div>
                  <div className="flex-1 text-foreground/90">{a.text}{a.actor ? <span className="text-muted-foreground"> · {a.actor}</span> : null}</div>
                </div>
              ))}
            </div>
          </div>
        </>
        )}

        <DialogFooter>
          {editing && <Button variant="destructive" onClick={() => onDelete(deal)} className="mr-auto" data-testid="deal-delete-btn"><Trash2 className="h-4 w-4 mr-1.5" />Delete</Button>}
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="deal-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="deal-save">{busy ? "Saving…" : editing ? "Save Changes" : "Add Deal"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
function DealCard({ deal, onOpen, onDragStart, onQuickStatus }) {
  const overdue = deal.next_action_date && !DEAD_STAGES.includes(deal.status) && deal.next_action_date < todayKey();
  const dueToday = deal.next_action_date === todayKey() && !DEAD_STAGES.includes(deal.status);
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, deal.id)}
      onClick={() => onOpen(deal)}
      className="group rounded-lg border border-border bg-card hover:border-primary/50 transition-colors cursor-pointer p-3 space-y-2"
      data-testid={`deal-card-${deal.id}`}
    >
      <div className="flex items-start gap-2">
        <GripVertical className="h-4 w-4 mt-0.5 text-muted-foreground/50 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-foreground truncate">{deal.property_label || "Untitled"}</div>
          {deal.owner && <div className="text-xs text-muted-foreground truncate flex items-center gap-1"><User className="h-3 w-3" />{deal.owner}</div>}
        </div>
      </div>
      <div className={`text-xs rounded-md px-2 py-1 flex items-center gap-1.5 ${overdue ? "bg-rose-500/10 text-rose-400" : dueToday ? "bg-amber-500/10 text-amber-400" : "bg-secondary/40 text-muted-foreground"}`}>
        <CalendarDays className="h-3 w-3 shrink-0" />
        <span className="truncate">{deal.next_action || "—"} · {fmtDate(deal.next_action_date)}</span>
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="flex items-center gap-1">{deal.outreach_method === "Cold Call" ? <Phone className="h-3 w-3" /> : deal.outreach_method === "Email" ? <MailIcon className="h-3 w-3" /> : null}{deal.outreach_method}</span>
        {(deal.target_offer || deal.max_offer) && <span className="font-medium text-foreground/80">{money(deal.target_offer)}{deal.max_offer ? ` / ${money(deal.max_offer)}` : ""}</span>}
      </div>
      <div className="flex items-center justify-between gap-2 pt-1">
        {deal.assigned_to_name ? <Badge variant="outline" className="text-[10px]">{deal.assigned_to_name}</Badge> : <span className="text-[10px] text-muted-foreground">Unassigned</span>}
        <div onClick={(e) => e.stopPropagation()}>
          <Select value={deal.status} onValueChange={(v) => onQuickStatus(deal, v)}>
            <SelectTrigger className="h-6 w-auto gap-1 px-1.5 text-[10px] border-border/60" data-testid={`deal-card-status-${deal.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STAGE_KEYS.map((s) => <SelectItem key={s} value={s} className="text-[11px]">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
export function AcquisitionTasks({ deals, onOpen, mineOnly, currentUserId }) {
  const active = deals.filter((d) => !DEAD_STAGES.includes(d.status) && d.next_action_date && (!mineOnly || d.assigned_to === currentUserId));
  const tk = todayKey();
  const overdue = active.filter((d) => d.next_action_date < tk).sort((a, b) => a.next_action_date.localeCompare(b.next_action_date));
  const today = active.filter((d) => d.next_action_date === tk);
  const upcoming = active.filter((d) => d.next_action_date > tk).sort((a, b) => a.next_action_date.localeCompare(b.next_action_date));

  const Row = ({ d }) => (
    <button onClick={() => onOpen(d)} className="w-full text-left flex items-center justify-between gap-3 rounded-md border border-border bg-card hover:border-primary/50 transition-colors px-3 py-2" data-testid={`task-row-${d.id}`}>
      <div className="min-w-0">
        <div className="text-sm text-foreground truncate"><span className="font-medium">{d.next_action}</span> — {d.property_label}</div>
        {d.owner && <div className="text-xs text-muted-foreground truncate">{d.owner}</div>}
      </div>
      <div className="text-xs text-muted-foreground shrink-0">{fmtDate(d.next_action_date)}</div>
    </button>
  );
  const Group = ({ icon, title, items, tone }) => (
    <div className="space-y-2" data-testid={`task-group-${title.toLowerCase()}`}>
      <div className={`flex items-center gap-2 text-sm font-semibold ${tone}`}>{icon}{title} <span className="text-muted-foreground font-normal">({items.length})</span></div>
      {items.length === 0 ? <div className="text-sm text-muted-foreground pl-6">Nothing here.</div> : <div className="space-y-1.5">{items.map((d) => <Row key={d.id} d={d} />)}</div>}
    </div>
  );
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Group icon={<AlertTriangle className="h-4 w-4" />} title="Overdue" items={overdue} tone="text-rose-400" />
      <Group icon={<Clock className="h-4 w-4" />} title="Today" items={today} tone="text-amber-400" />
      <Group icon={<CalendarDays className="h-4 w-4" />} title="Upcoming" items={upcoming} tone="text-primary" />
    </div>
  );
}

// -----------------------------------------------------------------------------
export default function AcquisitionPipeline() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { items, loading, reload } = useEntity(RESOURCE, { limit: 500, sort_by: "updated_at" });
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [tasksMine, setTasksMine] = useState(true);
  const [view, setView] = useState("table");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = usePersistedState("filters:acquisition:status", "all");
  const [ownerFilter, setOwnerFilter] = usePersistedState("filters:acquisition:owner", "all");

  const owners = useMemo(() => Array.from(new Set(items.map((d) => d.assigned_to_name).filter(Boolean))).sort(), [items]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((d) => {
      if (statusFilter !== "all" && d.status !== statusFilter) return false;
      if (ownerFilter !== "all" && d.assigned_to_name !== ownerFilter) return false;
      if (!q) return true;
      return [d.property_label, d.owner, d.city, d.state, d.assigned_to_name].filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [items, search, statusFilter, ownerFilter]);

  // Rank like the Property Analysis list: Angela verdict first, then blended margin/profit.
  const ranked = useMemo(() => {
    const rank = (r) => ({ "GO": 3, "CAUTION": 2, "NO-GO": 1 }[r.angela_verdict] || 0);
    const eff = (r) => {
      const s = r.subdivision;
      if (s && s.enabled && (s.lots || []).length > 0) {
        const arv = (s.lots || []).reduce((t, l) => t + (Number(l.arv) || 0), 0);
        const allIn = (s.lots || []).reduce((t, l) => t + (Number(l.cost) || 0), 0)
          + (s.site_costs || []).reduce((t, x) => t + (Number(x.amount) || 0), 0);
        return { arv, profit: arv - allIn };
      }
      return { arv: Number(r.arv_market_value) || 0, profit: Number(r.est_net_profit) || 0 };
    };
    const margin = (r) => { const e = eff(r); return e.arv > 0 ? e.profit / e.arv : -Infinity; };
    return [...filtered].sort((a, b) => rank(b) - rank(a) || margin(b) - margin(a) || eff(b).profit - eff(a).profit);
  }, [filtered]);
  const rankMap = useMemo(() => { const m = {}; ranked.forEach((r, i) => { m[r.id] = i + 1; }); return m; }, [ranked]);
  const RANK_COLUMN = {
    key: "ranked", label: "Ranked",
    render: (r) => {
      const n = rankMap[r.id];
      if (!n) return <span className="text-muted-foreground">—</span>;
      return <span data-testid={`rank-badge-${r.id}`} className="inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold bg-secondary text-foreground">{n}</span>;
    },
  };
  const tableColumns = [RANK_COLUMN, ...BASE_COLUMNS, VERDICT_COLUMN];

  useEffect(() => { api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);

  const byStage = useMemo(() => {
    const m = Object.fromEntries(STAGE_KEYS.map((k) => [k, []]));
    items.forEach((d) => { (m[d.status] || (m[d.status] = [])) && m[d.status].push(d); });
    return m;
  }, [items]);

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (d) => { navigate(`/record/${RESOURCE}/${d.id}`); };

  const moveStage = async (deal, status) => {
    if (deal.status === status) return;
    const activity = Array.isArray(deal.activity) ? [...deal.activity] : [];
    activity.push(logEntry(`Status: ${deal.status} → ${status}`, "status", user?.name));
    try { await updateItem(RESOURCE, deal.id, { status, activity }); reload(); }
    catch { toast.error("Could not move deal"); }
  };

  const onDrop = (e, status) => {
    e.preventDefault();
    setDragOverCol(null);
    const deal = items.find((d) => d.id === dragId);
    setDragId(null);
    if (deal) moveStage(deal, status);
  };

  const doDelete = async () => {
    try { await deleteItem(RESOURCE, confirmDel.id); toast.success("Deal deleted"); setConfirmDel(null); reload(); }
    catch { toast.error("Could not delete"); }
  };

  const myOverdue = items.filter((d) => !DEAD_STAGES.includes(d.status) && d.next_action_date && d.assigned_to === user?.id && d.next_action_date < todayKey()).length;

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto">
      <PageHeader
        title="Acquisition"
        count={items.length}
        subtitle="Outreach pipeline for on- and off-market deals"
        actions={
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-md border border-border p-0.5">
              <button onClick={() => setView("table")} className={`p-1.5 rounded ${view === "table" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} title="Table" data-testid="acq-view-table"><TableIcon className="h-4 w-4" /></button>
              <button onClick={() => setView("grid")} className={`p-1.5 rounded ${view === "grid" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} title="Grid" data-testid="acq-view-grid"><LayoutGrid className="h-4 w-4" /></button>
            </div>
            <Button onClick={openNew} data-testid="add-deal-btn"><Plus className="h-4 w-4 mr-1.5" />New Deal</Button>
          </div>
        }
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="pl-9" data-testid="acq-search" />
            </div>
            <Select value={ownerFilter} onValueChange={setOwnerFilter}>
              <SelectTrigger className="w-[200px]" data-testid="acq-owner-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All owners</SelectItem>
                {owners.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]" data-testid="acq-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STAGE_KEYS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {items.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-12 text-center">
              <p className="text-sm text-muted-foreground mb-3">No deals yet. Add your first property to start tracking outreach.</p>
              <Button onClick={openNew} data-testid="empty-add-deal-btn"><Plus className="h-4 w-4 mr-1.5" />Add Deal</Button>
            </div>
          ) : view === "table" ? (
            <DataTable columns={tableColumns} items={ranked} loading={loading} onRowClick={openEdit} emptyLabel="No deals match your filters" />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="acq-grid">
              {filtered.map((d) => (
                <DealCard key={d.id} deal={d} onOpen={openEdit} onDragStart={() => {}} onQuickStatus={moveStage} />
              ))}
            </div>
          )}

      <DealDialog open={dialogOpen} onOpenChange={setDialogOpen} deal={editing} users={users} currentUser={user} onSaved={() => reload()} onDelete={(d) => { setDialogOpen(false); setConfirmDel(d); }} />

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this deal?</AlertDialogTitle>
            <AlertDialogDescription>{confirmDel?.property_label} will be permanently removed from the pipeline.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-deal">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
